import {
  DEFAULT_QUOTE_TAX_RATE,
  QUOTE_FIELD_SOURCE_LABELS,
  type CatalogProduct,
  type QuoteDraft,
  type QuoteDraftClient,
  type QuoteDraftLine,
  type UpdateQuoteDraftBody,
} from '@cockpit/shared';

/**
 * Lot AT : aides de l'écran du brouillon de devis (saisie des nombres à la française, recherche dans le catalogue,
 * lignes ajoutées à la main, champs du bloc client).
 */

/** « 1 234,50 » → 1234.5 ; vide ou illisible → null. */
export function parseFrNumber(raw: string): number | null {
  const text = raw.replace(/[\s\u00a0\u202f€%]/g, '').replace(',', '.');
  if (text === '') return null;
  const value = Number(text);
  return Number.isFinite(value) ? value : null;
}

/** 290000 → « 2900 » ; 9999 → « 99,99 » (valeur d'un champ de prix). */
export function centsInput(cents: number): string {
  const euros = cents / 100;
  return Number.isInteger(euros) ? String(euros) : euros.toFixed(2).replace('.', ',');
}

/** 1.5 → « 1,5 » (quantité, remise). */
export function numberInput(value: number): string {
  return String(value).replace('.', ',');
}

const norm = (s: string) => s.toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '');

/** Produits actifs du catalogue qui contiennent les mots tapés (nom, code ou catégorie), 30 au plus. */
export function searchCatalog(items: readonly CatalogProduct[], query: string): CatalogProduct[] {
  const words = norm(query).split(/\s+/).filter(Boolean);
  return items
    .filter((p) => !p.disabled)
    .filter((p) => {
      const hay = norm(`${p.name} ${p.code ?? ''} ${p.category ?? ''}`);
      return words.every((w) => hay.includes(w));
    })
    .slice(0, 30);
}

/** Clé d'une nouvelle ligne (unique dans le brouillon). */
export function newLineKey(): string {
  return `u${Date.now().toString(36)}${Math.random().toString(36).slice(2, 6)}`;
}

/** Ligne ajoutée depuis la recherche du catalogue (« Ajoutée par toi »). */
export function lineFromProduct(product: CatalogProduct, key = newLineKey()): QuoteDraftLine {
  return {
    key,
    productId: product.id,
    productName: product.name,
    productCode: product.code,
    closestProductId: null,
    newProductConfirmed: false,
    name: product.name,
    description: null,
    quantity: 1,
    unitPriceCents: product.priceCents,
    discountPercent: 0,
    taxRate: product.taxRate,
    unit: product.unit,
    reason: null,
    doubt: null,
    addedBy: 'user',
  };
}

/** Prestation hors catalogue ajoutée à la main (à trancher : créer le produit ou prendre le plus proche). */
export function offCatalogLine(key = newLineKey()): QuoteDraftLine {
  return {
    key,
    productId: null,
    productName: null,
    productCode: null,
    closestProductId: null,
    newProductConfirmed: false,
    name: 'Prestation sur mesure',
    description: null,
    quantity: 1,
    unitPriceCents: 0,
    discountPercent: 0,
    taxRate: DEFAULT_QUOTE_TAX_RATE,
    unit: null,
    reason: null,
    doubt: null,
    addedBy: 'user',
  };
}

/**
 * « Prendre le produit le plus proche » : la ligne pointe vers ce produit du catalogue en GARDANT son titre, sa
 * description et son prix (décision de Maxime : on les modifie sur la ligne).
 */
export function takeClosestProduct(line: QuoteDraftLine, product: CatalogProduct): QuoteDraftLine {
  return {
    ...line,
    productId: product.id,
    productName: product.name,
    productCode: product.code,
    closestProductId: null,
    newProductConfirmed: false,
    taxRate: product.taxRate,
    unit: line.unit ?? product.unit,
  };
}

/** Libellé « registre public », « tes notes »… d'un champ pré-rempli encore intact, sinon null. */
export function sourceLabel(client: QuoteDraftClient, path: string): string | null {
  const source = client.sources[path];
  return source ? QUOTE_FIELD_SOURCE_LABELS[source] : null;
}

/** Copie modifiable d'un champ du bloc client : la valeur change et sa source (« registre public ») disparaît. */
export function setClientField(
  client: QuoteDraftClient,
  path: string,
  value: string | boolean,
): QuoteDraftClient {
  const next: QuoteDraftClient = JSON.parse(JSON.stringify(client)) as QuoteDraftClient;
  const [group, key] = path.split('.') as ['company' | 'contact', string];
  const target = next[group] as Record<string, unknown>;
  target[key] = typeof value === 'string' ? (value.trim() === '' ? null : value) : value;
  delete next.sources[path];
  return next;
}

/** Les champs modifiables du brouillon, tels qu'ils partent dans `PATCH /api/quote-drafts/:id`. */
export type QuoteEdit = Required<
  Pick<UpdateQuoteDraftBody, 'notes' | 'lines' | 'doubts' | 'comment' | 'client'>
>;

export function editOf(draft: QuoteDraft): QuoteEdit {
  return JSON.parse(
    JSON.stringify({
      notes: draft.notes,
      lines: draft.lines,
      doubts: draft.doubts,
      comment: draft.comment,
      client: draft.client,
    }),
  ) as QuoteEdit;
}

/** Champs qui diffèrent entre deux états (seuls ceux-là sont envoyés). */
export function changedFields(saved: QuoteEdit, current: QuoteEdit): UpdateQuoteDraftBody {
  const out: UpdateQuoteDraftBody = {};
  for (const key of ['notes', 'lines', 'doubts', 'comment', 'client'] as const) {
    if (JSON.stringify(saved[key]) !== JSON.stringify(current[key])) {
      (out as Record<string, unknown>)[key] = current[key];
    }
  }
  return out;
}

/** « 11 lignes » des notes collées. */
export function notesLineCount(notes: string): number {
  return notes.split('\n').filter((l) => l.trim() !== '').length;
}
